import type { ClientError, UpstreamError } from "./error.ts";
import type { Evidence } from "./evidence.ts";
import { isKeyOf } from "./is.ts";
import { integrationLabels } from "./workflow/constants.ts";
import type { ApprovalWithContainingRules } from "./workflow/evaluation/types.ts";
import type { RoutingRule } from "./workflow/types.ts";

export type PermissionType = keyof typeof integrationLabels;
export const isPermissionType = (value: string): value is PermissionType =>
  isKeyOf(integrationLabels, value);

export type ManageableRequest = {
  requestId: string;
  actions: ManageableAction[];
};
export type ManageableAction = "approvable" | "rejectable" | "revokable";

export type BasePermission = {
  approvedOnly?: boolean;
  commandLine?: string;
  duration?: string;
  reason?: string;
  requestedTimestamp: number;
};

export type Permission = {
  type: string;
  access: string;
  delegation: Record<string, unknown>;
  permission: Record<string, unknown>;
};

export type StagedPermission = Permission & {
  generated: Record<string, unknown> | object;
};

export type RequestId<T extends Permission> = T & {
  requestId: string;
};

export interface PermissionRequestError {
  type?: ClientError["type"] | UpstreamError["type"];
  message: string;
}

export type EmailNotificationData = Record<
  string, // the email address of the recipient
  {
    subject: string;
    messageId?: string;
  }
>;

export type EmailNotifications = {
  approvalEmailData?: EmailNotificationData;
  requestorEmailData?: EmailNotificationData;
};

export type MsTeamsNotifications = {
  /** A universally accessible URL for the message */
  approvalConversationUrl?: string;
  // TODO: use the actual type "Activity" from the MS Teams SDK.
  /** An activity reference to the channel message */
  approvalActivity?: any;
  /** A list of activity reference to the user messages */
  approvalActivityList?: any;
  /** An activity reference to the requestor's direct message */
  requestorActivity?: any;
};

export type WebexNotifications = {
  /** Message ID for the approval message in the Webex room */
  approvalMessageId?: string;
  /** Room ID where the approval message was sent */
  approvalRoomId?: string;
  /** List of message IDs for DMs sent to individual approvers */
  approvalMessageIds?: string[];
  /** List of messages (ID + roomId) for DMs sent to individual approvers */
  approvalMessages?: Array<{ messageId: string; roomId: string }>;
  /** Message ID for the requestor's direct message */
  requestorMessageId?: string;
  /** Room ID for the requestor's direct message */
  requestorRoomId?: string;
};

export type SlackNotifications = {
  /** Integration identifier for the approval message
   *
   * In Slack, this is `message_ts`
   */
  approvalMessageId?: string;
  /** A universally accessible URL for the message */
  approvalConversationUrl?: string;
  /** Info about all the approver notifications that were sent via DM, if applicable */
  approverNotifications?: SlackMessage[];
  /** Email of user that approved this request */
  approvalByEmail?: string;
  /** ID of the user that approved this request */
  approvalByUserId?: string;
  /** Timestamp when the request was approved */
  approvedTimestamp?: number;
  /** Channel ID of the requestor's DM */
  requestorChannelId?: string;
  /** Message ID of the requestor conversation */
  requestorMessageId?: string;
};

export type SlackMessage = { channelId: string; messageId: string };

// capture the approver details irrespective of where it was approved from
// in future the approval even if the approval happens from the web
// this removes the need for conditional checks to understand how the request got approved.
export type ApprovalDetails = {
  /** Source ID of the entity that approved this request */
  id: string;
  /** Name of the entity that approved this request */
  name?: string;
  /** Email of the user that approved this request */
  email?: string;
  /** Timestamp when the approver clicked the Approve button */
  approvedTimestamp?: number;
  /** Any additional information about how this request was approved. May or may
   * not be present depending on the approval source.
   */
  extra?: Record<string, string | undefined>;
  approvalSource: "evidence" | "pagerduty" | "persistent" | "slack" | "webapp";
};

export type EscalationDetails = {
  pagerduty: string[];
  incidentio: string[];
};

export type RequestNotifications = {
  msTeams?: MsTeamsNotifications;
  slack?: SlackNotifications;
  email?: EmailNotifications;
  webex?: WebexNotifications;
};

export type RequestNotificationKey = keyof RequestNotifications;
export type RequestNotificationValue =
  RequestNotifications[RequestNotificationKey];

// This is used to provide type-safety when performing operations against
// the notifications object. It allows us to use the keys of RequestNotifications
// as keys in the object, and ensures that the values are of the correct type.
export type RequestNotificationUnion = {
  [K in keyof RequestNotifications]: {
    key: K;
    value: RequestNotifications[K];
  };
}[keyof RequestNotifications];

export const UNKNOWN_IDENTITY_SENTINEL = "_unknown_";

export type RequestData = {
  error?: PermissionRequestError;
  expiryTimestamp?: number;
  grantTimestamp?: number; // Timestamp when the request was granted
  revokedTimestamp?: number; // Timestamp when the request was expired
  // Only those documents are retrieved by the expiry service where `isAwaitingExpiry`is true.
  // This field is not set (undefined) before a permission is granted in the target resource (DONE status).
  isAwaitingExpiry?: boolean;
  evidence?: Evidence[];
  lastUpdatedByUser?: string;
  lastUpdatedTimestamp?: number;
  notifications?: RequestNotifications;
  principal: string;
  requestor: string;
  /** If present, list of agents that initiated this request */
  agents?: string[];
  status: string; // Must be a "RequestStatus"
  /** If present, the time after which this request can no longer be approved */
  approvableUntil?: number;
  approvalDetails?: ApprovalDetails;
  canEscalate?: boolean;
  escalationDetails?: EscalationDetails;

  // Contains a snapshot of routing rules which applied to the given request, populated when the request is approved,
  // for audit purposes.
  routingRulesSnapshot?: RoutingRule[];
  // contains the approval rules that were applied to the request if the request is approved
  allowedApprovals?: ApprovalWithContainingRules[];
};
export type StagedPermissionRequest = BasePermission &
  StagedPermission &
  RequestData;

export type PermissionRequest = BasePermission & Permission & RequestData;

export const isStagedPermissionRequest = (
  request: PermissionRequest
): request is StagedPermissionRequest => "generated" in request;

// In order to update a nested path without overriding that path's parents, must
// use dot notation (see DocumentReference.update)
export type PermissionRequestUpdates = Partial<
  Omit<StagedPermissionRequest, "isAwaitingExpiry" | "notifications"> & {
    // TODO: use the actual type "Activity" from the MS Teams SDK. The SDK is not available in the shared package.
    "notifications.msTeams.requestorActivity": any;
    "notifications.msTeams.approvalActivity": any;
    "notifications.msTeams.approvalActivityList": any;
    "notifications.msTeams.approvalByUserId": string;
    "notifications.msTeams.approvalByEmail": string;
    "notifications.msTeams.approvedTimestamp": number;
    "notifications.msTeams.approvalConversationUrl": string;
    "notifications.slack.approvalMessageId": string;
    "notifications.slack.approvalByUserId": string;
    "notifications.slack.approvalByEmail": string;
    "notifications.slack.approvedTimestamp": number;
    "notifications.slack.approvalConversationUrl": string;
    "notifications.slack.requestorChannelId": string;
    "notifications.slack.approverNotifications": SlackMessage[];
    "notifications.slack.requestorMessageId": string;
    "notifications.email.requestorEmailData": EmailNotificationData;
    "notifications.email.approvalEmailData": EmailNotificationData;
    "notifications.webex.approvalMessageId": string;
    "notifications.webex.approvalRoomId": string;
    "notifications.webex.approvalMessageIds": string[];
    "notifications.webex.approvalMessages": Array<{
      messageId: string;
      roomId: string;
    }>;
    "notifications.webex.requestorMessageId": string;
    "notifications.webex.requestorRoomId": string;
    "approvalDetails.id": string;
    "approvalDetails.name": string;
    "approvalDetails.email": string;
    "approvalDetails.approvalSource": string;
    "approvalDetails.approvedTimestamp": number;
    "generated.publicKey": string;
  }
>;

export const PermissionKeys = ["permission", "type", "access"];
